import { useCallback, useMemo, useState } from 'react'
import { FolderOpen, Link2, Plus, Search, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { v7 as uuidv7 } from 'uuid'
import type { RelationType } from '@/lib/types/taskRelation'
import type { EnrichedTaskRelation } from '@/lib/db/rxdb/hooks/entities/task-relations'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible'
import { CollapsibleSectionHeader } from '@/components/app/layout/collapsible-section-header'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import {
  useAllWorkspaceTaskRelations,
  useProjects,
  useTaskMutations,
  useTaskRelationMutations, useTaskRelations, useTasks 
} from '@/lib/db/rxdb/hooks'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import useLoginStore from '@/store/LoginStore'
import {
  RELATION_LABELS,
  RELATION_TYPES,
  SYMMETRIC_RELATION_TYPES,
  getRelationLabel,
  isDirectionalRelationType,
  isUserEditableRelationType,
  wouldCreateCycle,
} from '@/lib/types/taskRelation'

interface RelationsSectionProps {
  taskId: string
  workspaceId: string | null | undefined
  onRelationClick?: (taskId: string) => void
}

export function RelationsSection({
  taskId,
  workspaceId,
  onRelationClick,
}: RelationsSectionProps) {
  const { t } = useTranslation(['tasks', 'common'])
  const [isAdding, setIsAdding] = useState(false)
  const [isOpen, setIsOpen] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedRelationType, setSelectedRelationType] = useState<string>(
    String(RELATION_TYPES.RELATED_TO),
  )
  const [pendingHierarchyRemoval, setPendingHierarchyRemoval] = useState<
    EnrichedTaskRelation | null
  >(null)

  const { relations, isLoading } = useTaskRelations(taskId)
  const { createTaskRelation, deleteTaskRelation } = useTaskRelationMutations()
  const { updateTask } = useTaskMutations()
  const { relations: allWorkspaceRelations } =
    useAllWorkspaceTaskRelations(workspaceId)
  const { tasks } = useTasks({ isDeleted: false })
  const { projects } = useProjects({ isDeleted: false })
  const userId = useLoginStore((state) => state.userId)

  // Build project lookup map
  const projectMap = useMemo(
    () => new Map(projects?.map((p) => [p.id, p]) ?? []),
    [projects],
  )

  // Get current task's project for cross-project detection
  const currentTask = useMemo(
    () => tasks.find((t) => t.id === taskId),
    [tasks, taskId],
  )

  // Filter tasks for the search picker (exclude current task and already-linked tasks)
  const linkedTaskIds = useMemo(
    () => new Set(relations.map((r) => r.linkedTaskId)),
    [relations],
  )

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return []
    const query = searchQuery.toLowerCase()
    return tasks
      .filter(
        (t) =>
          t.id !== taskId &&
          !linkedTaskIds.has(t.id) &&
          (!workspaceId || t.envId === workspaceId) &&
          (t.name.toLowerCase().includes(query) ||
            (t as any).identifier?.toLowerCase().includes(query)),
      )
      .slice(0, 10)
  }, [searchQuery, tasks, taskId, linkedTaskIds, workspaceId])

  // Group search results by project for better cross-project visibility
  const groupedSearchResults = useMemo(() => {
    const groups = new Map<string, typeof searchResults>()
    for (const task of searchResults) {
      const key = task.projectId ?? '__no_project__'
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key)!.push(task)
    }
    // Sort: current project first, then alphabetically by project name
    return [...groups.entries()].sort(([a], [b]) => {
      if (a === currentTask?.projectId) return -1
      if (b === currentTask?.projectId) return 1
      const nameA = projectMap.get(a)?.name ?? ''
      const nameB = projectMap.get(b)?.name ?? ''
      return nameA.localeCompare(nameB)
    })
  }, [searchResults, currentTask?.projectId, projectMap])

  const handleAddRelation = useCallback(
    async (targetTaskId: string) => {
      if (!workspaceId || !userId) return

      const targetTask = tasks.find((t) => t.id === targetTaskId)
      const sourceTask = tasks.find((t) => t.id === taskId)
      const relationType = Number(selectedRelationType) as RelationType

      // Defensive: search picker already excludes the current task, but a
      // self-link must never be created.
      if (targetTaskId === taskId) {
        toast.error(t('relations.errors.selfLink'))
        return
      }

      if (
        relationType === RELATION_TYPES.PARENT_CHILD &&
        targetTask?.parentId &&
        targetTask.parentId !== taskId
      ) {
        toast.error(t('relations.errors.alreadyHasParent'))
        return
      }

      // Check for duplicate (both directions for symmetric types)
      const isDuplicate = relations.some((r) => {
        if (r.linkedTaskId !== targetTaskId) return false
        if (SYMMETRIC_RELATION_TYPES.includes(relationType as any)) {
          return r.relationType === relationType
        }
        return (
          r.relationType === relationType && r.direction === 'outgoing'
        )
      })

      if (isDuplicate) return

      // Cycle prevention for directional types only. Backend enforces this
      // authoritatively; this is a UX guard to fail fast before the optimistic
      // local write and subsequent push rejection.
      if (
        isDirectionalRelationType(relationType) &&
        wouldCreateCycle(
          allWorkspaceRelations,
          taskId,
          targetTaskId,
          relationType,
        )
      ) {
        toast.error(t('relations.errors.circularDependency'))
        return
      }

      const now = Date.now()
      await createTaskRelation({
        id: uuidv7(),
        envId: workspaceId,
        sourceTaskId: taskId,
        sourceProjectId: sourceTask?.projectId ?? null,
        targetTaskId: targetTaskId,
        targetProjectId: targetTask?.projectId ?? null,
        relationType,
        isDeleted: false,
        sourceTaskName: sourceTask?.name ?? '',
        sourceIdentifier: (sourceTask as any)?.identifier ?? null,
        targetTaskName: targetTask?.name ?? '',
        targetIdentifier: (targetTask as any)?.identifier ?? null,
        createdOn: now,
        createdBy: userId,
        updatedOn: now,
        updatedBy: userId,
      })

      if (relationType === RELATION_TYPES.PARENT_CHILD) {
        await updateTask(targetTaskId, { parentId: taskId })
      }

      setSearchQuery('')
      setIsAdding(false)
    },
    [
      workspaceId,
      userId,
      taskId,
      tasks,
      relations,
      allWorkspaceRelations,
      selectedRelationType,
      createTaskRelation,
      updateTask,
      t,
    ],
  )

  const handleRemoveRelation = useCallback(
    async (relationId: string, unparentTaskId?: string) => {
      if (unparentTaskId) {
        await updateTask(unparentTaskId, { parentId: null })
      }
      await deleteTaskRelation(relationId)
    },
    [deleteTaskRelation, updateTask],
  )

  const confirmHierarchyRemoval = useCallback(
    async () => {
      if (!pendingHierarchyRemoval) return
      const childTaskId =
        pendingHierarchyRemoval.direction === 'outgoing'
          ? pendingHierarchyRemoval.targetTaskId
          : pendingHierarchyRemoval.sourceTaskId

      await handleRemoveRelation(pendingHierarchyRemoval.id, childTaskId)
      setPendingHierarchyRemoval(null)
    },
    [handleRemoveRelation, pendingHierarchyRemoval],
  )

  if (isLoading) return null

  // Group relations by type for display
  const groupedRelations = relations.reduce(
    (acc, rel) => {
      const label = getRelationLabel(rel.relationType, rel.direction)
      if (!acc[label]) acc[label] = []
      acc[label].push(rel)
      return acc
    },
    {} as Record<string, typeof relations>,
  )

  return (
    <div>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleSectionHeader
          icon={<Link2 className="size-4" />}
          title="Relations"
          count={relations.length > 0 ? `(${relations.length})` : undefined}
          isOpen={isOpen}
          rightSlot={
            <Popover open={isAdding} onOpenChange={setIsAdding}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-6 text-muted-foreground hover:text-foreground hover:bg-accent"
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsAdding(true)
                  }}
                >
                  <Plus className="size-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-3" align="start">
            <div className="space-y-3">
              <Select
                value={selectedRelationType}
                onValueChange={setSelectedRelationType}
              >
                <SelectTrigger className="w-full h-8 text-base md:text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(RELATION_LABELS)
                    .filter(([value]) =>
                      isUserEditableRelationType(Number(value)),
                    )
                    .map(([value, labels]) => (
                      <SelectItem key={value} value={value}>
                        {labels.outgoing}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>

              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                <Input
                  placeholder="Search tasks..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 h-8 text-sm"
                  autoFocus
                />
              </div>

              {groupedSearchResults.length > 0 && (
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {groupedSearchResults.map(([projectId, projectTasks]) => {
                    const project = projectMap.get(projectId)
                    const isCrossProject = projectId !== currentTask?.projectId
                    return (
                      <div key={projectId}>
                        {groupedSearchResults.length > 1 && (
                          <div className="flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground">
                            <FolderOpen className="size-3" />
                            <span className="truncate font-medium">
                              {project?.name ?? 'No project'}
                              {isCrossProject && projectId !== '__no_project__' && (
                                <span className="ml-1 text-[10px] bg-muted px-1 py-0.5 rounded">
                                  cross-project
                                </span>
                              )}
                            </span>
                          </div>
                        )}
                        {projectTasks.map((task) => (
                          <button
                            key={task.id}
                            type="button"
                            className="w-full text-left px-2 py-1.5 text-sm rounded-md hover:bg-accent cursor-pointer truncate"
                            onClick={() => void handleAddRelation(task.id)}
                          >
                            {(task as any).identifier && (
                              <span className="text-muted-foreground mr-1.5">
                                {(task as any).identifier}
                              </span>
                            )}
                            {task.name}
                          </button>
                        ))}
                      </div>
                    )
                  })}
                </div>
              )}

              {searchQuery.trim() && searchResults.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-2">
                  No tasks found
                </p>
              )}
            </div>
              </PopoverContent>
            </Popover>
          }
        />
        <CollapsibleContent className="pt-2">
          {relations.length === 0 ? (
            <p className="text-sm text-muted-foreground px-2 py-1">
              No relations yet
            </p>
          ) : (
            <div className="space-y-2">
              {Object.entries(groupedRelations).map(([label, rels]) => (
              <div key={label} className="space-y-0.5">
                <p className="text-xs font-medium text-muted-foreground px-2 uppercase tracking-wide">
                  {label}
                </p>
                {rels.map((rel) => {
                  const linkedProject = rel.linkedProjectId
                    ? projectMap.get(rel.linkedProjectId)
                    : null
                  const isCrossProject =
                    rel.linkedProjectId != null &&
                    rel.linkedProjectId !== currentTask?.projectId
                  return (
                    <div
                      key={rel.id}
                      className="group flex items-center gap-2 px-2 py-1 rounded-md hover:bg-accent"
                    >
                      <button
                        type="button"
                        className="flex-1 text-left text-sm truncate cursor-pointer"
                        onClick={() => onRelationClick?.(rel.linkedTaskId)}
                      >
                        {rel.linkedIdentifier && (
                          <span className="text-muted-foreground mr-1.5">
                            {rel.linkedIdentifier}
                          </span>
                        )}
                        {rel.linkedTaskName || 'Untitled task'}
                        {isCrossProject && linkedProject && (
                          <span className="ml-1.5 text-xs text-muted-foreground">
                            <FolderOpen className="inline size-3 mr-0.5 -mt-0.5" />
                            {linkedProject.name}
                          </span>
                        )}
                      </button>
                      {isUserEditableRelationType(rel.relationType) && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-6 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => {
                            if (rel.relationType === RELATION_TYPES.PARENT_CHILD) {
                              setPendingHierarchyRemoval(rel)
                              return
                            }
                            void handleRemoveRelation(rel.id)
                          }}
                        >
                          <X className="size-3" />
                        </Button>
                      )}
                    </div>
                  )
                })}
              </div>
              ))}
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
      <AlertDialog
        open={pendingHierarchyRemoval != null}
        onOpenChange={(open) => {
          if (!open) setPendingHierarchyRemoval(null)
        }}
      >
        <AlertDialogContent onClick={(event) => event.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('dialogs.unlinkSubtask.title')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('dialogs.unlinkSubtask.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('common:actions.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.stopPropagation()
                void confirmHierarchyRemoval()
              }}
            >
              {t('actions.unlinkSubtask')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
